import React, { useState, useMemo } from 'react';
import { SidebarModal } from './SidebarModal';
import SearchInput from '../../../components/ui/SearchInput';
import { useCrmData } from '../../../contexts/CrmProviders';
import { AppIcon } from '../../../components/icons/AppIcon';
import { cleanPhone } from '../../../utils/phoneUtils';
import { ChatConversation } from '../hooks/useChat';

interface NewChatModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (phone: string, name: string) => void;
    conversations?: ChatConversation[];
}

export const NewChatModal: React.FC<NewChatModalProps> = ({ isOpen, onClose, onSubmit, conversations = [] }) => {
    const { contacts } = useCrmData();
    const [searchTerm, setSearchTerm] = useState('');

    const filteredContacts = useMemo(() => {
        if (!searchTerm.trim()) return [];

        const lowerTerm = searchTerm.toLowerCase();
        const matchingContacts = contacts.filter(contact =>
        (contact.name?.toLowerCase().includes(lowerTerm) ||
            contact.phone?.includes(lowerTerm) ||
            contact.whatsapp?.includes(lowerTerm))
        );

        return matchingContacts.filter(contact => {
            const contactPhone = cleanPhone(contact.whatsapp || contact.phone || '');
            return !!contactPhone;
        });
    }, [contacts, searchTerm, conversations]);

    const handleContactClick = (contact: any) => {
        const phone = contact.whatsapp || contact.phone;
        if (phone) {
            const clean = phone.replace(/\D/g, '');
            const finalPhone = clean.length <= 11 ? `55${clean}` : clean;
            onSubmit(finalPhone, contact.name);
            onClose();
            setSearchTerm('');
        }
    };

    return (
        <SidebarModal isOpen={isOpen} onClose={onClose} title="Nova Conversa">
            <div className="flex flex-col h-full">
                <div className="mb-4">
                    <SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="Buscar contato..."
                        autoFocus
                    />
                </div>

                <div className="flex-1 overflow-y-auto -mx-4 px-4 custom-scrollbar">
                    {filteredContacts.length > 0 && (
                        <div className="mb-2 px-2 text-[10px] font-bold text-muted uppercase tracking-wider font-mono">
                            CONTATOS ENCONTRADOS
                        </div>
                    )}

                    {filteredContacts.length > 0 ? (
                        <div className="space-y-1">
                            {filteredContacts.map(contact => (
                                <button
                                    key={contact.id}
                                    onClick={() => handleContactClick(contact)}
                                    className="w-full text-left px-3 py-3 hover:bg-muted/5 rounded-lg flex items-center gap-3 transition-all duration-200 group border border-transparent hover:border-border hover:shadow-sm"
                                >
                                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0 font-mono">
                                        {contact.name?.substring(0, 2).toUpperCase() || '??'}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className="text-sm font-semibold text-text truncate">
                                            {contact.name || 'Sem nome'}
                                        </h4>
                                        <p className="text-xs text-muted truncate mt-0.5 font-mono">
                                            {contact.whatsapp || contact.phone || 'Iniciar conversa'}
                                        </p>
                                    </div>
                                    <div className="text-muted group-hover:text-primary transition-colors">
                                        <AppIcon name="message-plus" className="w-5 h-5" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        searchTerm.trim() && (
                            <div className="text-center py-8 text-muted text-sm">
                                Nenhum contato encontrado
                            </div>
                        )
                    )}

                    {!searchTerm.trim() && (
                        <div className="text-center py-12 text-muted text-sm flex flex-col items-center opacity-70">
                            <AppIcon name="users" className="w-12 h-12 mb-3 text-muted/30" />
                            <p className="font-semibold text-text">Digite para buscar</p>
                            <p className="text-xs mt-1">Busque por nome ou telefone</p>
                        </div>
                    )}
                </div>
            </div>
        </SidebarModal>
    );
};
